import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, GraduationCap, Building2, Mail, Lock, User, Loader2, KeyRound } from "lucide-react";
import { Logo } from "../components/shared/Logo";
import { GridBg } from "../components/shared/GridBg";
import { ThemeToggle } from "../components/shared/ThemeToggle";
import { useAuth } from "../context/AppContext";
import api, { startGoogleLogin } from "../lib/api";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";
import { toast } from "sonner";

const errMsg = (d) => {
  if (!d) return "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return d?.msg || JSON.stringify(d);
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Auth() {
  const { user, loading, setUser, refresh } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("email"); // email | google
  const [mode, setMode] = useState("login"); // login | register | verify
  const [role, setRole] = useState("student");
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && user) {
      if (!user.role) navigate("/select-role", { replace: true });
      else if (!user.onboarding_complete && user.role !== "admin")
        navigate(user.role === "student" ? "/student/onboarding" : "/company/onboarding", { replace: true });
      else navigate(`/${user.role}`, { replace: true });
    }
  }, [user, loading, navigate]);

  const routeUser = (u) => {
    if (!u.role) return navigate("/select-role", { replace: true });
    if (!u.onboarding_complete && u.role !== "admin")
      return navigate(u.role === "student" ? "/student/onboarding" : "/company/onboarding", { replace: true });
    navigate(`/${u.role}`, { replace: true });
  };

  const doLogin = async (e) => {
    e?.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email: f.email, password: f.password });
      if (data.session_token) localStorage.setItem("session_token", data.session_token);
      setUser(data.user); toast.success("Welcome back"); routeUser(data.user);
    } catch (e) {
      const detail = errMsg(e.response?.data?.detail);
      if (e.response?.status === 403 && /verified/i.test(detail)) {
        setMode("verify"); toast("Please verify your email first");
      } else toast.error(detail);
    } finally { setBusy(false); }
  };

  const doRegister = async (e) => {
    e?.preventDefault(); setBusy(true);
    try {
      await api.post("/auth/register", { email: f.email, password: f.password, name: f.name, role });
      toast.success("Check your email", { description: "We sent a 6-digit code." });
      setMode("verify");
    } catch (e) { toast.error(errMsg(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doVerify = async (e) => {
    e?.preventDefault();
    if (otp.length !== 6) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-email", { email: f.email, otp });
      if (data.session_token) localStorage.setItem("session_token", data.session_token);
      setUser(data.user); toast.success("Email verified"); routeUser(data.user);
    } catch (e) { toast.error(errMsg(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const resend = async () => {
    try { await api.post("/auth/resend-otp", { email: f.email }); toast.success("Code resent"); }
    catch (e) { toast.error(errMsg(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" style={{ background: "var(--bg)" }}>
      <GridBg hero className="hidden lg:flex flex-col p-10 border-r" style={{ borderColor: "var(--border)" }}>
        <div><Logo/></div>
        <div className="mt-auto max-w-md">
          <div className="eyebrow text-blue-500">Assisto4U · Marketplace</div>
          <h2 className="mt-3 font-display text-3xl leading-tight" style={{ color: "var(--text)" }}>Find work. Build experience. Get paid.</h2>
          <p className="mt-4 text-sm" style={{ color: "var(--text-muted)" }}>Every student is Aadhaar-verified. Every company is GST-verified. Every task is admin-approved.</p>
          <div className="mt-10 card-surface p-4">
            <div className="flex items-center gap-3">
              <img alt="" src="https://images.unsplash.com/photo-1580489944761-15a19d654956?crop=entropy&cs=srgb&fm=jpg&w=100&h=100&fit=crop" className="w-10 h-10 rounded-full object-cover"/>
              <div className="min-w-0">
                <div className="text-sm" style={{ color: "var(--text)" }}>"Paid ₹42,000 this semester without missing a lecture."</div>
                <div className="text-xs mt-0.5" style={{ color: "var(--text-dim)" }}>Ananya R. · Symbiosis Pune</div>
              </div>
            </div>
          </div>
        </div>
      </GridBg>

      <div className="relative flex items-center justify-center p-6 sm:p-10">
        <div className="absolute top-4 right-4"><ThemeToggle/></div>
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Logo/></div>

          <div className="flex items-center gap-1 p-1 rounded-full w-fit mb-6" style={{ background: "var(--surface)", border: "1px solid #27272A" }}>
            <button data-testid="auth-tab-email" onClick={() => { setTab("email"); setMode("login"); }} className={`px-4 py-1.5 text-xs rounded-full transition-colors duration-200 ${tab === "email" ? "bg-blue-500/20 text-blue-300" : "text-zinc-400"}`}>Email</button>
            <button data-testid="auth-tab-google" onClick={() => setTab("google")} className={`px-4 py-1.5 text-xs rounded-full transition-colors duration-200 ${tab === "google" ? "bg-blue-500/20 text-blue-300" : "text-zinc-400"}`}>Google</button>
          </div>

          {tab === "google" && (
            <>
              <h1 className="font-display text-3xl text-white">Sign in with Google</h1>
              <p className="text-sm text-zinc-500 mt-2">One-click sign in. Admin roles are provisioned by whitelist.</p>
              <button data-testid="google-login-btn" onClick={startGoogleLogin}
                      className="mt-8 w-full py-3 rounded-md text-sm font-medium inline-flex items-center justify-center gap-3 border transition-colors duration-200 hover:bg-white/[0.03]"
                      style={{ background: "#FFFFFF", color: "#111", borderColor: "var(--border-strong)" }}>
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35.5 24 35.5c-6.4 0-11.6-5.2-11.6-11.5S17.6 12.5 24 12.5c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.8 6.5 29.1 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5c10.8 0 19.5-8.7 19.5-19.5 0-1.2-.1-2.3-.4-3.5z"/>
                  <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.7 19 12.5 24 12.5c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.8 6.5 29.1 4.5 24 4.5 16.4 4.5 9.9 8.7 6.3 14.7z"/>
                  <path fill="#4CAF50" d="M24 43.5c5 0 9.6-1.9 13-5l-6-5.1c-2 1.4-4.4 2.2-7 2.2-5.3 0-9.7-3-11.3-7.4l-6.5 5C9.9 39.3 16.4 43.5 24 43.5z"/>
                  <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4-4 5.4l6 5.1c-.4.4 6.7-4.9 6.7-14.5 0-1.2-.1-2.3-.4-3.5z"/>
                </svg>
                Continue with Google
              </button>
            </>
          )}

          {tab === "email" && mode === "verify" && (
            <>
              <h1 className="font-display text-3xl text-white">Verify your email</h1>
              <p className="text-sm text-zinc-500 mt-2">Enter the 6-digit code sent to <span className="text-zinc-300">{f.email}</span>.</p>
              <form onSubmit={doVerify} className="mt-8 space-y-5">
                <div className="flex justify-center">
                  <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="verify-otp-input">
                    <InputOTPGroup>
                      {[0,1,2,3,4,5].map(i => <InputOTPSlot key={i} index={i} className="border-[#27272A] bg-[#111114] text-white"/>)}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <button data-testid="verify-otp-btn" type="submit" disabled={busy || otp.length !== 6} className="btn-primary w-full py-2.5 rounded-md text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50">
                  {busy ? <Loader2 size={13} className="animate-spin"/> : <KeyRound size={13}/>} Verify & continue
                </button>
                <div className="flex items-center justify-between text-xs">
                  <button type="button" onClick={() => setMode("login")} className="text-zinc-500 hover:text-zinc-300 transition-colors duration-200">← Back to sign in</button>
                  <button type="button" data-testid="resend-otp-btn" onClick={resend} className="text-blue-400 hover:text-blue-300 transition-colors duration-200">Resend code</button>
                </div>
              </form>
            </>
          )}

          {tab === "email" && mode !== "verify" && (
            <>
              <div className="flex items-center gap-1 p-1 rounded-full w-fit mb-4" style={{ background: "var(--surface)", border: "1px solid #27272A" }}>
                <button data-testid="mode-login"    onClick={() => setMode("login")}    className={`px-4 py-1.5 text-xs rounded-full transition-colors duration-200 ${mode === "login" ? "bg-white/10 text-white" : "text-zinc-400"}`}>Sign in</button>
                <button data-testid="mode-register" onClick={() => setMode("register")} className={`px-4 py-1.5 text-xs rounded-full transition-colors duration-200 ${mode === "register" ? "bg-white/10 text-white" : "text-zinc-400"}`}>Create account</button>
              </div>

              <h1 className="font-display text-3xl text-white">{mode === "login" ? "Welcome back" : "Create your account"}</h1>
              <p className="text-sm text-zinc-500 mt-2">
                {mode === "login" ? "Sign in with your email and password." : "Register with email — we'll send a 6-digit code."}
              </p>

              {mode === "register" && (
                <div className="mt-6 grid grid-cols-2 gap-2">
                  {[{k:"student",l:"Student",I:GraduationCap},{k:"company",l:"Company",I:Building2}].map(({k,l,I}) => {
                    const on = role === k;
                    return (
                      <button key={k} data-testid={`auth-role-${k}`} type="button" onClick={() => setRole(k)}
                              className={`p-3 rounded-md border text-left transition-colors duration-200 ${on ? "border-blue-500/40 bg-blue-500/10" : "border-[#27272A] bg-[#0A0A0C] hover:border-[#3A3A42]"}`}>
                        <I size={14} className={on ? "text-blue-400" : "text-zinc-400"}/>
                        <div className={`text-xs mt-1.5 ${on ? "text-white" : "text-zinc-300"}`}>{l}</div>
                      </button>
                    );
                  })}
                </div>
              )}

              <form onSubmit={mode === "login" ? doLogin : doRegister} className="mt-6 space-y-3">
                {mode === "register" && (
                  <Field icon={User} label="Full name">
                    <input data-testid="auth-name-input" value={f.name} onChange={e => setF(x => ({...x, name: e.target.value}))} placeholder="Your name" required minLength={1} className={ic}/>
                  </Field>
                )}
                <Field icon={Mail} label="Email">
                  <input data-testid="auth-email-input" value={f.email} onChange={e => setF(x => ({...x, email: e.target.value}))} type="email" required placeholder="you@work.com" className={ic}/>
                </Field>
                <Field icon={Lock} label="Password">
                  <input data-testid="auth-password-input" value={f.password} onChange={e => setF(x => ({...x, password: e.target.value}))} type="password" required minLength={8} placeholder="••••••••" className={ic}/>
                </Field>

                <button data-testid="auth-submit-btn" type="submit" disabled={busy} className="btn-primary w-full py-2.5 rounded-md text-sm font-medium inline-flex items-center justify-center gap-2 mt-2 disabled:opacity-50">
                  {busy ? <Loader2 size={13} className="animate-spin"/> : <ArrowRight size={14}/>}
                  {mode === "login" ? "Sign in" : "Create account"}
                </button>
              </form>
            </>
          )}

          <div className="mt-8 text-xs text-zinc-500 text-center">
            By continuing you agree to Assisto4U's <a href="#" className="text-zinc-300">Terms</a> and <a href="#" className="text-zinc-300">Privacy</a>.
          </div>
          <div className="mt-6 text-center">
            <Link to="/" className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors duration-200">← Back to home</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

const ic = "flex-1 bg-transparent outline-none text-sm text-white placeholder:text-zinc-600";
const Field = ({ icon: Icon, label, children }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-widest text-zinc-500">{label}</span>
    <div className="mt-1.5 flex items-center gap-2 px-3 py-2.5 rounded-md border transition-colors duration-200 focus-within:border-blue-500/50"
         style={{ borderColor: "var(--border-strong)", background: "var(--surface)" }}>
      <Icon size={14} className="text-zinc-500"/>
      {children}
    </div>
  </label>
);
